import { EIconButtons } from 'ui/EasTable/EasTableRow/EasTableValue/constants';
import {
	ETableStructureAlign,
	ETableStructureType,
	ItemWithId,
	TTableStructure,
	TTableStructureButton,
	TTableStructureColors,
	TTableStructureFilter,
} from 'ui/EasTable/types';

/** `requires_customer_action` → `Requires customer action`. */
export const humanize = (value: string | null | undefined): string =>
	value ? value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) : '—';

export type TPgTone = 'success' | 'error' | 'warning' | 'info' | 'default';

const TONE_BY_VALUE: Record<string, TPgTone> = {};
const addTone = (tone: TPgTone, values: string[]) => {
	values.forEach((value) => {
		TONE_BY_VALUE[value] = tone;
	});
};
addTone('success', [
	'succeeded',
	'done',
	'processed',
	'settled',
	'completed',
	'sent',
	'active',
	'resolved',
	'accepted',
	'ok',
]);
addTone('error', [
	'failed',
	'dead',
	'cancelled',
	'cancel_unknown',
	'critical',
	'exhausted',
	'dd_failed',
	'overdue',
	'conflict',
	'rejected',
	'disabled',
]);
addTone('warning', [
	'pending',
	'queued',
	'warning',
	'open',
	'unknown',
	'manual_review',
	'requires_customer_action',
	'awaiting_customer',
	'confirming',
	'waiting_funds',
	'skipped',
	'no_method',
	'card_retrying',
	'dd_pending',
	'partial',
	'manual_required',
	'error',
]);
addTone('info', [
	'created',
	'requested',
	'received',
	'scheduled',
	'info',
	'intent_created',
	'processing',
	'running',
	'acknowledged',
]);

/** Tone of a status value (shared by chips and table colours). */
export const toneOf = (value: string | null | undefined): TPgTone =>
	(value && TONE_BY_VALUE[value]) || 'default';

const TABLE_COLOR: Record<TPgTone, string> = {
	success: 'success.light',
	error: 'error.light',
	warning: 'warning.light',
	info: 'info.light',
	default: 'info.light',
};

/** `filter` and `colors` of a `STATUS` column for a list of enum values. */
export const statusColumn = (
	values: string[],
	labels?: Record<string, string>,
): { filter: TTableStructureFilter; colors: TTableStructureColors } => ({
	filter: values.map((value) => [value, labels?.[value] ?? humanize(value)]),
	colors: values.map((value) => [value, TABLE_COLOR[toneOf(value)]]),
});

/** Filter options of a yes/no column the gateway filters as `true` / `false`. */
export const YES_NO_FILTER: TTableStructureFilter = [
	['true', 'Yes'],
	['false', 'No'],
];

/** Column with the common defaults (left aligned, not sortable, not filterable). */
export const pgColumn = <T extends ItemWithId>(
	name: string,
	selector: keyof T & string,
	type: ETableStructureType,
	extra: Partial<TTableStructure<keyof T, T>> = {},
): TTableStructure<keyof T, T> => ({
	name,
	selector,
	show: true,
	sortable: false,
	filterable: false,
	type,
	align: ETableStructureAlign.LEFT,
	...extra,
});

/** Right-aligned buttons column; `handleAction` receives the button `name`. */
export const pgActionsColumn = <T extends ItemWithId>(
	buttons: TTableStructureButton[],
	buttonFilter?: (item: T) => TTableStructureButton[],
): TTableStructure<keyof T, T> => ({
	name: 'Actions',
	selector: 'actions',
	noName: true,
	show: true,
	sortable: false,
	filterable: false,
	type: ETableStructureType.BUTTONS,
	align: ETableStructureAlign.RIGHT,
	buttons,
	buttonFilter,
});

export const DETAILS_BUTTON: TTableStructureButton = {
	name: 'Details',
	label: 'Details',
	icon: EIconButtons.SUMMARIZE,
};
